import {
  exportOrders,
  useGetOrders,
  getGetOrdersQueryOptions,
} from "@/requests/gen/react-query/order";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import {
  OrderStatusEnum,
  EntityTypeEnum,
  Task,
} from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamDateFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { format, parseISO } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { useState } from "react";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { ExportCSV } from "@/components/ui/csv-export";

const orderSearchParsers = {
  statuses: parseAsArrayOf(parseAsStringEnum(Object.values(OrderStatusEnum))),
};

const orderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...orderSearchParsers,
};

const loadOrderSearch = createLoader(orderSearchAllParsers);

type OrderSearch = inferParserType<typeof orderSearchAllParsers>;

function orderQueryParams(
  p: OrderSearch,
): Parameters<typeof getGetOrdersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    order_statuses: p.statuses,
    practice_ids: p.practice_ids,
  };
}

export const Route = createFileRoute("/_private/_patient-360/order/lookup")({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetOrdersQueryOptions(
        orderQueryParams(loadOrderSearch(location.searchStr)),
      ),
    ),
  component: OrderLookup,
});

// Define the columns for the table
interface OrderTableProps {
  link?: LinkProps;
  order_id: string;
  created_at: string;
  patient_name: string;
  doctor_name: string;
  status: OrderStatusEnum | "";
  tasks: Task[];
}

// Define the columns headers and accessor keys
const columns: ColumnDef<OrderTableProps>[] = [
  { header: "Order ID", accessorKey: "order_id" },
  { header: "Patient", accessorKey: "patient_name" },
  { header: "Doctor", accessorKey: "doctor_name" },
  { header: "Practice", accessorKey: "practice" },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
    enableSorting: true,
  },
  { header: "Status", accessorKey: "status" },
  {
    header: "Tasks",
    accessorKey: "tasks",
    cell: (cell) => {
      const tasks = cell.getValue() as Task[];

      const incompleteTasks = tasks.filter(
        (task) => task.completed_at === null,
      );
      if (incompleteTasks.length === 0) {
        return "";
      }
      const firstTask = incompleteTasks.at(0)?.task;
      const remainingCount = incompleteTasks.length - 1;

      return (
        <div className="flex items-center gap-1">
          <Badge variant={"secondary"}>{firstTask}</Badge>
          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger className="bg-primary rounded-full text-white size-5 flex items-center justify-center text-xs">
                +{remainingCount}
              </TooltipTrigger>
              <TooltipContent>
                {incompleteTasks
                  .slice(1)
                  .map((task) => task.task)
                  .join(", ")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
];

// Main component
function OrderLookup() {
  const [queryParams, setQueryParams] = useQueryStates(orderSearchAllParsers);

  // Get the orders
  const orders = useGetOrders(orderQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: OrderTableProps[] | undefined = orders.data?.items.map(
    (order) => {
      return {
        link: linkOptions({
          to: "/order/$orderId",
          params: { orderId: String(order.order_id) },
        }),
        order_id: order.order_id,
        practice: order.practice.name,
        patient_name: `${order.patient.last_name}, ${order.patient.first_name} (${format(parseISO(order.patient.date_of_birth), "MM-dd-yyyy")})`,
        doctor_name: `${order.doctor.last_name}, ${order.doctor.first_name}`,
        created_at: order.created_at,
        status: order.status,
        tasks: order.tasks,
      };
    },
  );

  // State for search terms
  const [practiceSearch, setPracticeSearch] = useState<string | undefined>();

  // Get practice entities for filtering
  const practices = useGetEntitiesInfinite(
    {
      entity_types: [EntityTypeEnum.Practice],
      search: practiceSearch,
      include_ids: queryParams.practice_ids || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  return (
    <div className="text-left">
      <title>Orders - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Orders", link: { to: "/order/lookup" } }]} />
      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
          <CardDescription>
            View recent orders below. You can search, filter, and click to see
            more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/order/create">
                <Plus />
                Create Order
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={orders.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(OrderStatusEnum).map((status) => {
                    return { label: status, value: status };
                  }),
                },
              ]}
              values={queryParams.statuses || []}
              onChange={(values) => setQueryParams({ statuses: values })}
            />
            <DropdownFilter
              title="Practice"
              options={[
                {
                  items:
                    practices.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => ({
                        label: entity.name,
                        value: entity.entity_id,
                      })) ?? [],
                },
              ]}
              values={queryParams.practice_ids || []}
              onChange={(values) => setQueryParams({ practice_ids: values })}
              search={practiceSearch}
              setSearch={setPracticeSearch}
              isFetching={practices.isFetchingNextPage}
              fetchNextPage={practices.fetchNextPage}
            />
            <ParamDateFilter title="Created At" name="created_at" />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={orders.isLoading}
            isError={orders.isError}
            count={orders.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportOrders({
                    search: queryParams.search,
                    sort_column: queryParams.sort_column,
                    ascending: queryParams.ascending,
                    created_at_start: queryParams.created_at_start,
                    created_at_end: queryParams.created_at_end,
                    order_statuses: queryParams.statuses,
                    practice_ids: queryParams.practice_ids,
                    hidden_columns: queryParams.hidden,
                  })
                }
                filename="orders"
                requiresPHIWarning={true}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
